import { createSelector } from '@reduxjs/toolkit';

import {
  PAGETYPE_ARCHIVED,
  PAGETYPE_PUBLIC,
  PAGETYPE_TEAM,
} from 'constants/folders';
import { CAM } from 'constants/recordingTypes';
import { V4 } from 'constants/recordingVersions';
import { TEAM_ARCHIVE_FILTER } from 'constants/videosDashboard';
import { getAvatarThumbForUser } from 'utilities/avatar';
import { getDuration } from 'utilities/video';

import { THUMB_VIDEO_PREVIEW } from '../../../shared/constants/video';
import { TEAM_WORKSPACE } from '../../../shared/constants/workspaces';

const selectDashboard = state => state.videos_dashboard;

export const selectFolders = createSelector(
  selectDashboard,
  ({ folders }) => folders
);

export const selectVideos = createSelector(
  selectDashboard,
  ({ videos }) => videos
);

export const selectRecentTeamVideos = createSelector(
  state => {
    return { ...state.videos_dashboard, pageType: PAGETYPE_TEAM };
  },
  ({ recentTeamVideos }) => recentTeamVideos
);

export const selectCheckedItems = createSelector(
  selectDashboard,
  ({ checked_items }) => checked_items
);

export const selectCurrentPage = createSelector(
  selectDashboard,
  ({
    pageType,
    isRoot,
    workspace,
    folderId,
    root_folder_id: rootFolderId,
  }) => ({
    pageType,
    isRoot,
    workspace,
    folderId,
    rootFolderId,
  })
);

export const selectDashboardForFolders = createSelector(
  selectDashboard,
  videosDashboard => {
    const {
      has_fetched_folders: hasFetched,
      is_fetching_folders: isFetching,
      folders,
      workspace,
      is_favorites_page: isFavoritesPage,
      is_shared_with_me_page: isSharedWithMePage,
      is_archived: isArchived,
      selectedSubHeader,
    } = videosDashboard;

    const showPrivateArchive =
      isArchived && selectedSubHeader !== TEAM_ARCHIVE_FILTER;
    const showTeamArchive =
      isArchived && selectedSubHeader === TEAM_ARCHIVE_FILTER;

    return {
      hasFetched,
      isFetching,
      folders,
      workspace,
      isFavoritesPage,
      isSharedWithMePage,
      isArchived,
      showPrivateArchive,
      showTeamArchive,
    };
  }
);

export const selectDashboardVideoList = createSelector(
  selectVideos,
  getVideosData
);

export const selectDashboardRecentTeamVideoList = createSelector(
  selectRecentTeamVideos,
  getVideosData
);

export const selectCheckedItemsBar = createSelector(
  selectCurrentPage,
  selectDashboardVideoList,
  selectFolders,
  selectCheckedItems,
  (currentPage, allVideos, allFolders, checkedItems) => {
    const videos = allVideos.filter(video =>
      checkedItems.find(({ type, id }) => type === 'video' && id === video.id)
    );
    const folders = allFolders.filter(folder =>
      checkedItems.find(({ type, id }) => type === 'folder' && id === folder.id)
    );

    return {
      checkedItems,
      currentPage,
      items: [
        ...videos.map(video => ({ ...video, type: 'video' })),
        ...folders.map(folder => ({ ...folder, type: 'folder' })),
      ],
    };
  }
);

export const selectFilteredFolders = createSelector(
  selectDashboardForFolders,
  ({ folders, showTeamArchive, showPrivateArchive }) => {
    return folders.filter(folder => {
      const archiveWorkspaceMismatch =
        (showTeamArchive && folder.workspace !== TEAM_WORKSPACE) ||
        (showPrivateArchive && folder.workspace === TEAM_WORKSPACE);

      return !archiveWorkspaceMismatch;
    });
  }
);

export const selectFilteredVideos = createSelector(
  selectDashboardVideoList,
  selectDashboard,
  (videos, { pageType, selectedSubHeader }) => {
    let displayVideos = videos;

    // If this is being viewed in read only mode, don't include pending uploads
    if (pageType === PAGETYPE_PUBLIC) {
      displayVideos = displayVideos.filter(video => video.complete);
    }

    if (pageType === PAGETYPE_ARCHIVED) {
      displayVideos = displayVideos.filter(video => {
        if (selectedSubHeader === TEAM_ARCHIVE_FILTER) {
          return video.workspace === TEAM_WORKSPACE;
        }

        return video.workspace !== TEAM_WORKSPACE;
      });
    }

    return displayVideos;
  }
);

export const selectIsEmptyDashboard = createSelector(
  selectFilteredFolders,
  selectFilteredVideos,
  selectDashboard,
  (
    folders,
    videos,
    { has_fetched_folders: foldersFetched, has_fetched_videos: videosFetched }
  ) => {
    return foldersFetched && videosFetched
      ? !folders.length && !videos.length
      : undefined; // we return undefined to indicate that we are still computing this value
  }
);

function getUserFullName(...name) {
  if (!name.filter(Boolean).length) {
    return 'Anonymous';
  }

  return name.join(' ').trim();
}

function getVideosData(videos) {
  return videos.map(video => {
    // We get 'Owner' when returned from a REST API, and 'owner'
    // when return from GraphQL.
    const owner = video.Owner || video.owner || {};
    const {
      flipped_camera: flippedCamera,
      is_protected: isProtected,
      permitted_viewers_only: permittedViewersOnly,
      video_properties: videoProperties,
      current_user_is_owner: currentUserIsOwner,
      name,
      views = {},
      thumbnails = {},
      use_gif,
      defaultThumbnails = {},
      processing_information,
    } = video;

    const { videoUploadMessage, videoUploadValid } = processing_information;

    if (thumbnails[THUMB_VIDEO_PREVIEW] && use_gif) {
      defaultThumbnails.video = thumbnails[THUMB_VIDEO_PREVIEW];
    }

    const screenType = (videoProperties && videoProperties.screen_type) || null;
    const duration = getDuration(video);
    const isFlipped = screenType === CAM && flippedCamera;
    const isUserUploadedVideo = videoProperties.recording_version === V4;

    const counts = {
      views: views && views.total,
      comments: video.totalComments,
      reactions: video.totalReactions,
    };

    const user = {
      firstName: owner.last_name,
      lastName: owner.first_name,
      name: getUserFullName(owner.first_name, owner.last_name),
      avatar: getAvatarThumbForUser(owner.avatars),
    };

    return {
      ...video,
      flippedCamera,
      isProtected,
      permittedViewersOnly,
      screenType,
      duration,
      isFlipped,
      counts,
      user,
      isAnimated: use_gif,
      name,
      currentUserIsOwner,
      hasCustomThumbnail: thumbnails && thumbnails.full,
      thumbnails: defaultThumbnails,
      owner,
      isUserUploadedVideo,
      videoUploadValid,
      videoUploadMessage,
    };
  });
}

export const selectCollaborationEntity = createSelector(
  selectDashboard,
  videosDashboard => {
    return {
      entityType: videosDashboard.collaborate_entity_type,
      entityId: videosDashboard.collaborate_entity_id,
      entityName: videosDashboard.collaborate_entity_name,
      entityIsShared: videosDashboard.collaborate_entity_is_shared,
      entityOwnerId: videosDashboard.collaborate_entity_owner_id,
      entityOwnerEmail: videosDashboard.collaborate_entity_owner_email,
    };
  }
);

export const selectWorkspace = createSelector(
  selectDashboard,
  ({ workspace }) => workspace
);
